import { PyrenzSlider } from '@client/theme';
import { HelpOutlineOutlined as HelpIcon } from '@mui/icons-material';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useState } from 'react';

interface SliderComponentProps {
  sliderKey: string;
  stateValue: number;
  stateSetter: (value: number) => void;
  sliderDescriptions: Record<string, string>;
  setShowPopover: (key: string | null) => void;
  maxValue?: number;
  step?: number;
  marks?: Array<{ value: number; label: string }>;
  className?: string;
}

export function SliderComponent({
  sliderKey,
  stateValue = 0,
  stateSetter,
  sliderDescriptions,
  setShowPopover,
  maxValue,
  step,
  marks,
  className,
}: SliderComponentProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box key={sliderKey} className={className} sx={{ mb: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="body2" color="text.secondary">
          {sliderKey.charAt(0).toUpperCase() +
            sliderKey.slice(1).replace(/([A-Z])/g, ' $1')}
        </Typography>
        <Box
          component="button"
          aria-owns={open ? 'mouse-over-popover' : undefined}
          aria-haspopup="true"
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          onClick={(e) => {
            handlePopoverOpen(e);
            setShowPopover(sliderKey);
          }}
          sx={{
            color: 'text.secondary',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            '&:hover': { color: 'text.primary' },
            '&:focus': { outline: 'none' },
          }}
        >
          <HelpIcon fontSize="small" />
        </Box>
        <Popover
          id="mouse-over-popover"
          sx={{ pointerEvents: 'none' }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <Typography
            sx={{ p: 1 }}
            className="bg-white text-black p-2 rounded shadow-md"
          >
            {sliderDescriptions[sliderKey]}
          </Typography>
        </Popover>
      </Box>
      <PyrenzSlider
        value={stateValue}
        onChange={(event, value) => stateSetter(value as number)}
        max={maxValue ?? 100}
        min={
          sliderKey.includes('Penalty')
            ? -2
            : sliderKey === 'modelMemoryLimit'
              ? 15
              : 0
        }
        step={
          step ??
          (sliderKey === 'maxTokens' ? 1 : sliderKey === 'topP' ? 0.01 : 0.1)
        }
        marks={marks}
        valueLabelDisplay="auto"
        sx={{ mt: 1 }}
      />
    </Box>
  );
}
